import type { FeatureFlagsConfig, FeatureFlagsFetcher, FeatureFlagsResponse } from './types/feature-flags.types';

/**
 * Fetch the flag list. Never throws — a failed load resolves to the config's default flags, so
 * no caller needs a try/catch around a feature check.
 *
 * `no-store` because a stale flag list is worse than an extra request, and because Next would
 * otherwise cache this inside a Server Component. Repeat calls are collapsed by the TTL in
 * `loadFeatureFlags`, not by the HTTP cache.
 */
export async function fetchFlags(
  config: FeatureFlagsConfig,
  fetcher: FeatureFlagsFetcher = defaultFetcher,
): Promise<FeatureFlagsResponse> {
  const defaults = config.defaultFlags ?? {};

  try {
    const request = fetcher(config.apiUrl);
    const flags = config.timeout ? await Promise.race([request, rejectAfter(config.timeout)]) : await request;

    return { ...defaults, ...flags };
  } catch (error) {
    console.error('[feature-flags] Error loading feature flags:', error);
    config.onError?.(error);

    return defaults;
  }
}

async function defaultFetcher(url: string): Promise<FeatureFlagsResponse> {
  const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

function rejectAfter(ms: number): Promise<never> {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error(`Feature flags request timed out after ${ms}ms`)), ms),
  );
}
